import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { z } from "zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, ArrowRight } from "lucide-react";

// /invoice  — landing page when a user lands on the bare /invoice URL
// (e.g. typo, an old email link, or a stale bookmark). Supports an optional
// ?ref= query so old links keep working and otherwise points users at their
// "My Payments" dashboard.
export const Route = createFileRoute("/invoice/")({
  validateSearch: (s: Record<string, unknown>) =>
    z
      .object({
        ref: z.string().trim().min(4).max(80).optional(),
        reference: z.string().trim().min(4).max(80).optional(),
      })
      .parse(s),
  beforeLoad: ({ search }) => {
    const ref = search.ref || search.reference;
    if (ref) {
      throw redirect({
        to: "/invoice/$ref",
        params: { ref },
      });
    }
  },
  component: InvoiceIndex,
});

function InvoiceIndex() {
  return (
    <div className="mx-auto max-w-2xl px-4 py-12">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" /> Invoice not specified
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          <p>
            Invoices are opened from a unique reference link like{" "}
            <code className="rounded bg-muted px-1 py-0.5">/invoice/&lt;reference&gt;</code>.
            Use the link sent to your email after placing an order, or open
            your payments dashboard below.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <Link to="/portal/my-payments">
                View my payments <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/contracts">Browse contracts</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/portal/credits">Buy credits</Link>
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
